import { type HelmInspect, toHelmInspect } from '@features/fields/api/useHelmInspect'
import {
  BoundaryHoleBehavior,
  DEFAULT_GLOBAL_SETTINGS,
  type GuidancePlan,
  GuidancePrimitive
} from 'api-validations/locutus/guidance'
import { convertToImperial } from 'utils'

import type { FormValues, ZoneGuidance } from './formtypes'
import { cornerKey, corridorKey, edgeKey, holeKey } from './overrideKeys'

export type PlanSettings = Pick<
  FormValues,
  | 'implementWidth'
  | 'implementWidthUnit'
  | 'rowSpacing'
  | 'rowSpacingUnit'
  | 'numberOfHeadlands'
  | 'cornerType'
  | 'boundaryHoleBehavior'
  | 'edgeHeadlands'
  | 'cornerOverrides'
  | 'holeOverrides'
  | 'corridorOverrides'
>

const toBehavior = (passable: boolean) =>
  passable ? BoundaryHoleBehavior.Passable : BoundaryHoleBehavior.Impassable

export const buildDraftGuidancePlan = ({
  boundaryId,
  settings,
  inspect,
  regionIds,
  zoneGuidance,
  lines
}: {
  boundaryId: string
  settings: PlanSettings
  inspect: HelmInspect
  regionIds: string[]
  zoneGuidance: Record<string, ZoneGuidance | undefined>
  lines: { id: string; name: string; type: GuidancePrimitive }[]
}): GuidancePlan | null => {
  const implementWidth = convertToImperial(settings.implementWidth, settings.implementWidthUnit)
  const rowSpacing = convertToImperial(settings.rowSpacing, settings.rowSpacingUnit)
  if (
    !Number.isFinite(implementWidth) ||
    !Number.isFinite(rowSpacing) ||
    !Number.isFinite(settings.numberOfHeadlands)
  ) {
    return null
  }

  const infillRegions = regionIds.flatMap((regionId) => {
    const lineId = zoneGuidance[regionId]?.id
    const line = lines.find((l) => l.id === lineId)
    if (line === undefined || line.type === GuidancePrimitive.PrePropagated) return []
    return [{ regionId, infill: { id: line.id, name: line.name, type: line.type } }]
  })
  if (infillRegions.length === 0) return null

  const edges = inspect.edges.flatMap((edge) => {
    const count = settings.edgeHeadlands.find((o) => o.key === edgeKey(edge))?.count
    return count === undefined || !Number.isFinite(count)
      ? []
      : [
          {
            id: edge.id,
            headlandCount: count,
            headlandRotation: DEFAULT_GLOBAL_SETTINGS.exteriorHeadlandRotation,
            headlandOrder: DEFAULT_GLOBAL_SETTINGS.exteriorHeadlandOrder
          }
        ]
  })
  const corners = inspect.corners.flatMap((corner) => {
    const cornerType = settings.cornerOverrides.find((o) => o.key === cornerKey(corner))?.cornerType
    return cornerType === undefined ? [] : [{ id: corner.id, cornerType }]
  })
  const holes = inspect.holes.flatMap((hole) => {
    const passable = settings.holeOverrides.find((o) => o.key === holeKey(hole))?.passable
    return passable === undefined ? [] : [{ id: hole.id, behavior: toBehavior(passable) }]
  })
  const corridors = inspect.corridors.flatMap((corridor) => {
    const passable = settings.corridorOverrides.find(
      (o) => o.key === corridorKey(corridor)
    )?.passable
    return passable === undefined ? [] : [{ id: corridor.id, behavior: toBehavior(passable) }]
  })

  return {
    boundaryId,
    fieldFeatures: inspect.regions,
    implementWidth,
    rowSpacing,
    globalSettings: {
      ...DEFAULT_GLOBAL_SETTINGS,
      exteriorHeadlandCount: settings.numberOfHeadlands,
      cornerType: settings.cornerType,
      boundaryHoleBehavior: settings.boundaryHoleBehavior
    },
    overrides: {
      ...(edges.length > 0 && { edges }),
      ...(corners.length > 0 && { corners }),
      ...(holes.length > 0 && { holes }),
      ...(corridors.length > 0 && { corridors })
    },
    infillRegions
  }
}

export const overrideSettingsFromPlan = (
  plan: GuidancePlan
): Pick<
  PlanSettings,
  'edgeHeadlands' | 'cornerOverrides' | 'holeOverrides' | 'corridorOverrides'
> => {
  const saved = toHelmInspect({
    features: plan.fieldFeatures,
    boundary: { type: 'FeatureCollection', features: [] }
  })
  return {
    edgeHeadlands: (plan.overrides.edges ?? []).flatMap((override) => {
      const edge = saved.edges.find((e) => e.id === override.id)
      return edge === undefined ? [] : [{ key: edgeKey(edge), count: override.headlandCount }]
    }),
    cornerOverrides: (plan.overrides.corners ?? []).flatMap((override) => {
      const corner = saved.corners.find((c) => c.id === override.id)
      return corner === undefined
        ? []
        : [{ key: cornerKey(corner), cornerType: override.cornerType }]
    }),
    holeOverrides: (plan.overrides.holes ?? []).flatMap((override) => {
      const hole = saved.holes.find((h) => h.id === override.id)
      return hole === undefined
        ? []
        : [{ key: holeKey(hole), passable: override.behavior === BoundaryHoleBehavior.Passable }]
    }),
    corridorOverrides: (plan.overrides.corridors ?? []).flatMap((override) => {
      const corridor = saved.corridors.find((c) => c.id === override.id)
      return corridor === undefined
        ? []
        : [
            {
              key: corridorKey(corridor),
              passable: override.behavior === BoundaryHoleBehavior.Passable
            }
          ]
    })
  }
}
